import { useCallback, useEffect, useRef, useState, type FocusEvent } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Menu, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import {
  detailedIndustryGuides,
  getCtaLabel,
  localize,
  serviceCatalogue,
  siteContent,
  type ServiceRole,
} from "@/content/site";
import { useTranslation } from "@/i18n/useTranslation";
import PrimaryCtaLink from "@/components/PrimaryCtaLink";
import VertLogo from "@/components/VertLogo";
import Link from "@/components/LocalizedLink";
import { pairedLocalePath, stripLocalePrefix } from "@/i18n/localePath";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { utilityRouteCopy } from "@/content/utilityRouteCopy";

type MenuKey = "services" | "industries";

type NavigationItem = {
  href: string;
  label: string;
  description: string;
  role?: ServiceRole;
  eyebrow?: string;
};

const isActivePath = (pathname: string, href: string) => (
  href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`)
);

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<MenuKey | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const dropdownButtonRefs = useRef<Record<MenuKey, HTMLButtonElement | null>>({
    services: null,
    industries: null,
  });
  const mobileHashNavigationRef = useRef(false);
  const closeMenuTimeoutRef = useRef<number | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const activePathname = stripLocalePrefix(location.pathname);
  const { theme, setTheme } = useTheme();
  const { t, locale, setLocale } = useTranslation();
  const copy = utilityRouteCopy.navbar[locale];

  const serviceLinks = serviceCatalogue.map((service) => ({
    href: service.href,
    label: localize(service.name, locale),
    description: localize(service.description, locale),
    role: service.role,
  }));

  const industryLinks = detailedIndustryGuides.map((industry) => ({
    href: industry.href,
    label: localize(industry.label, locale),
    description: localize(industry.description, locale),
    eyebrow: copy.detailedGuide,
  }));
  const beyondCommerceLink: NavigationItem = {
    href: "/industries#additional-contexts",
    label: copy.beyondCommerce,
    description: copy.beyondCommerceDescription,
  };

  const directLinks = [
    {
      href: "/solutions",
      label: t("nav.solutions"),
      description: copy.directDescriptions.solutions,
    },
    {
      href: "/work",
      label: t("nav.work"),
      description: copy.directDescriptions.work,
    },
    {
      href: "/insights",
      label: t("nav.insights"),
      description: copy.directDescriptions.insights,
    },
    {
      href: "/about",
      label: t("nav.about"),
      description: copy.directDescriptions.about,
    },
  ];

  const clearCloseMenuTimeout = useCallback(() => {
    if (closeMenuTimeoutRef.current) {
      window.clearTimeout(closeMenuTimeoutRef.current);
      closeMenuTimeoutRef.current = null;
    }
  }, []);

  const openDesktopMenu = useCallback((menuKey: MenuKey) => {
    clearCloseMenuTimeout();
    setActiveMenu(menuKey);
  }, [clearCloseMenuTimeout]);

  const closeDesktopMenu = useCallback(() => {
    clearCloseMenuTimeout();
    setActiveMenu(null);
  }, [clearCloseMenuTimeout]);

  const scheduleDesktopMenuClose = useCallback((menuKey: MenuKey) => {
    clearCloseMenuTimeout();
    closeMenuTimeoutRef.current = window.setTimeout(() => {
      closeMenuTimeoutRef.current = null;
      setActiveMenu((currentMenu) => (currentMenu === menuKey ? null : currentMenu));
    }, 150);
  }, [clearCloseMenuTimeout]);

  useEffect(() => setMounted(true), []);

  useEffect(() => clearCloseMenuTimeout, [clearCloseMenuTimeout]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    closeDesktopMenu();
    window.scrollTo(0, 0);
  }, [closeDesktopMenu, location.pathname]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const menuToRestore = activeMenu;
        clearCloseMenuTimeout();
        setActiveMenu(null);

        window.requestAnimationFrame(() => {
          if (menuToRestore) {
            dropdownButtonRefs.current[menuToRestore]?.focus();
          }
        });
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [activeMenu, clearCloseMenuTimeout]);

  const closeDesktopMenuOnBlur = (event: FocusEvent<HTMLDivElement>) => {
    clearCloseMenuTimeout();
    const nextTarget = event.relatedTarget;
    if (!(nextTarget instanceof Node) || !event.currentTarget.contains(nextTarget)) {
      closeDesktopMenu();
    }
  };

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");
  const targetLocale = locale === "en" ? "fr" : "en";
  const pairedPathWithoutState = pairedLocalePath(location.pathname, targetLocale, "", "");
  const pairedPath = pairedLocalePath(
    location.pathname,
    targetLocale,
    // Do not copy query strings into rendered language-switch links. Utility
    // routes can carry private tokens, and reflecting them into the DOM makes
    // those values available to injected scripts and HTML capture tooling.
    "",
    location.hash,
  );
  const isTokenizedUnsubscribe = activePathname === "/unsubscribe" && Boolean(location.search);
  const handleTokenSafeLocaleSwitch = () => {
    setLocale(targetLocale);
    navigate({
      pathname: pairedPathWithoutState,
      search: location.search,
      hash: location.hash,
    });
  };
  const renderLocaleSwitch = (className: string) => (
    isTokenizedUnsubscribe ? (
      <button
        type="button"
        onClick={handleTokenSafeLocaleSwitch}
        className={className}
        aria-label={copy.switchLocaleAria}
        lang={targetLocale === "fr" ? "fr-CA" : "en-CA"}
      >
        {copy.targetLocaleLabel}
      </button>
    ) : (
      <RouterLink
        to={pairedPath}
        onClick={() => setLocale(targetLocale)}
        className={className}
        aria-label={copy.switchLocaleAria}
        lang={targetLocale === "fr" ? "fr-CA" : "en-CA"}
        hrefLang={targetLocale === "fr" ? "fr-CA" : "en-CA"}
      >
        {copy.targetLocaleLabel}
      </RouterLink>
    )
  );
  const primaryCtaLabel = getCtaLabel(siteContent.ctas.primary, locale, "shortLabel");
  const isDarkTheme = !mounted || theme === "dark";
  const topText = scrolled || !isDarkTheme ? "text-foreground" : "text-[hsl(var(--sd-fg))]";
  const topMuted = scrolled || !isDarkTheme ? "text-content-secondary" : "text-content-on-dark";
  const topHover = scrolled || !isDarkTheme ? "hover:text-foreground" : "hover:text-[hsl(var(--sd-fg))]";
  const navShell = scrolled
    ? "border-border/45 bg-background/78"
    : isDarkTheme
      ? "border-[hsl(var(--sd-fg)/0.1)] bg-[hsl(var(--control-panel)/0.2)]"
      : "border-border/55 bg-background/82 shadow-[0_14px_34px_hsl(var(--control-ink)/0.07)] backdrop-blur-xl";

  const renderDesktopDropdown = (
    menuKey: MenuKey,
    label: string,
    overview: NavigationItem,
    links: NavigationItem[],
    followUp?: NavigationItem,
  ) => {
    const open = activeMenu === menuKey;
    const active = links.some((link) => isActivePath(activePathname, link.href)) || isActivePath(activePathname, overview.href);
    const overviewLink = (
      <Link
        to={overview.href}
        onClick={closeDesktopMenu}
        aria-current={isActivePath(activePathname, overview.href) ? "page" : undefined}
        className={`focus-ring group block p-4 transition-colors duration-300 hover:bg-surface-1 ${
          menuKey === "services" ? "mt-2 border-t border-border/25" : "border-b border-border/25"
        }`}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
          {copy.overview}
        </span>
        <span className="mt-2 block text-[14px] font-semibold leading-snug text-foreground group-hover:text-primary">
          {overview.label}
        </span>
        <span className="mt-1.5 block max-w-md text-[12.5px] leading-[1.65] text-muted-foreground">
          {overview.description}
        </span>
      </Link>
    );

    return (
      <div
        className="relative"
        onMouseEnter={() => openDesktopMenu(menuKey)}
        onMouseLeave={() => scheduleDesktopMenuClose(menuKey)}
        onFocus={() => openDesktopMenu(menuKey)}
        onBlur={closeDesktopMenuOnBlur}
      >
        <button
          ref={(node) => {
            dropdownButtonRefs.current[menuKey] = node;
          }}
          type="button"
          onClick={() => {
            if (open) {
              closeDesktopMenu();
            } else {
              openDesktopMenu(menuKey);
            }
          }}
          onFocus={() => openDesktopMenu(menuKey)}
          aria-expanded={open}
          aria-haspopup="true"
          aria-controls={`${menuKey}-navigation`}
          aria-current={active ? "page" : undefined}
          className={`focus-ring relative inline-flex h-10 items-center gap-1.5 px-3 font-mono text-[10.5px] font-medium uppercase tracking-[0.12em] transition-colors duration-400 ${topHover} ${
            active
              ? scrolled || !isDarkTheme
                ? "bg-foreground/5 text-foreground"
                : "bg-[hsl(var(--sd-fg)/0.06)] text-[hsl(var(--sd-fg))]"
              : topMuted
          }`}
        >
          {label}
          <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
          {active && <span className="absolute -bottom-1 left-0 right-0 h-px bg-primary/40" />}
        </button>

        {open && (
          <>
            <div
              aria-hidden="true"
              className="absolute left-0 top-full z-40 h-3 w-[min(34rem,calc(100vw-2rem))]"
            />
            <div
              id={`${menuKey}-navigation`}
              className="absolute left-0 top-full z-50 mt-3 w-[min(34rem,calc(100vw-2rem))] border border-border/35 bg-background/95 p-2 shadow-[0_24px_70px_hsl(var(--control-ink)/0.18)] backdrop-blur-xl"
            >
              {menuKey !== "services" ? overviewLink : null}
              <ul className={`grid grid-cols-1 gap-1 sm:grid-cols-2 ${menuKey === "services" ? "" : "pt-2"}`}>
                {links.map((link) => (
                  <li
                    key={`${menuKey}-${link.href}-${link.label}`}
                    className={link.role === "review" ? "sm:col-span-2" : undefined}
                  >
                    <Link
                      to={link.href}
                      onClick={closeDesktopMenu}
                      aria-current={isActivePath(activePathname, link.href) ? "page" : undefined}
                      className={`focus-ring group block min-h-[5.5rem] p-4 transition-colors duration-300 hover:bg-surface-1 ${
                        link.role === "review" ? "border-l-2 border-primary/65 bg-primary/[0.05]" : ""
                      }`}
                    >
                      {link.role === "review" ? (
                        <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.14em] text-primary">
                          {copy.specializedDecisionReview}
                        </span>
                      ) : link.eyebrow ? (
                        <span className="mb-2 block font-mono text-[9px] uppercase tracking-[0.14em] text-primary">
                          {link.eyebrow}
                        </span>
                      ) : null}
                      <span className="block text-[13px] font-semibold leading-snug text-content-secondary group-hover:text-primary">
                        {link.label}
                      </span>
                      <span className="mt-1.5 block text-[12px] leading-[1.6] text-content-secondary">
                        {link.description}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              {followUp ? (
                <Link
                  to={followUp.href}
                  onClick={closeDesktopMenu}
                  className="focus-ring group mt-2 flex items-center justify-between gap-6 border-t border-border/35 p-4 transition-colors duration-300 hover:bg-surface-1"
                >
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.14em] text-primary">
                      {followUp.label}
                    </span>
                    <span className="mt-1.5 block max-w-md text-[12.5px] leading-[1.6] text-content-secondary">
                      {followUp.description}
                    </span>
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-content-secondary transition-transform duration-300 group-hover:translate-x-1 group-hover:text-primary" aria-hidden="true" />
                </Link>
              ) : null}
              {menuKey === "services" ? overviewLink : null}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderMobileGroup = (
    label: string,
    overview: NavigationItem,
    links: NavigationItem[],
    followUp?: NavigationItem,
  ) => {
    const hasReview = links.some((link) => link.role === "review");
    const overviewLink = (
      <Link
        to={overview.href}
        onClick={() => setMobileOpen(false)}
        aria-current={isActivePath(activePathname, overview.href) ? "page" : undefined}
        className="focus-ring mt-3 block border-l border-primary/45 bg-primary/[0.035] py-3 pl-4 pr-3 text-[14px] font-semibold text-foreground"
      >
        {overview.label}
        <span className="mt-1 block text-[12.5px] font-normal leading-[1.55] text-muted-foreground">
          {overview.description}
        </span>
      </Link>
    );

    return (
    <details className="group border-b border-border/20 py-2.5">
      <summary
        aria-label={copy.showMenu(label)}
        className="focus-ring flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 px-3 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-foreground transition-colors hover:bg-surface-1 [&::-webkit-details-marker]:hidden"
      >
        <span>{label}</span>
        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-300 group-open:rotate-180" aria-hidden="true" />
      </summary>
      <div className="pb-3 pt-2">
        {overviewLink}
        <ul className="mt-3 space-y-1">
          {links.map((link) => (
            <li key={`${label}-${link.href}-${link.label}`}>
              <Link
                to={link.href}
                onClick={() => setMobileOpen(false)}
                aria-current={isActivePath(activePathname, link.href) ? "page" : undefined}
                className={`focus-ring min-h-11 px-4 py-2 text-[13.5px] leading-snug transition-colors duration-300 hover:bg-surface-1 hover:text-foreground ${
                  link.role === "review"
                    ? "mb-2 block border-l-2 border-primary/65 bg-primary/[0.05] font-semibold text-foreground"
                    : "flex items-center text-muted-foreground"
                }`}
              >
                {link.role === "review" ? (
                  <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.12em] text-primary">
                    {copy.specializedReview}
                  </span>
                ) : null}
                <span className={link.eyebrow ? "block" : undefined}>
                  {link.eyebrow ? (
                    <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.12em] text-primary">
                      {link.eyebrow}
                    </span>
                  ) : null}
                  {link.label}
                </span>
              </Link>
            </li>
          ))}
        </ul>
        {followUp ? (
          <Link
            to={followUp.href}
            onClick={() => {
              mobileHashNavigationRef.current = followUp.href.includes("#");
              setMobileOpen(false);
            }}
            className="focus-ring mx-4 mt-4 flex min-h-14 items-center justify-between gap-4 border-t border-border/35 py-3 text-foreground"
          >
            <span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.12em] text-primary">
                {followUp.label}
              </span>
              <span className="mt-1 block text-[12.5px] leading-[1.55] text-muted-foreground">
                {followUp.description}
              </span>
            </span>
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-content-secondary" aria-hidden="true" />
          </Link>
        ) : null}
        {hasReview ? (
          <p className="mt-4 px-4 text-[13px] leading-6 text-muted-foreground">
            {copy.reviewBoundary}
          </p>
        ) : null}
      </div>
    </details>
    );
  };

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 transition-all duration-700 ease-out ${
        scrolled
          ? "glass border-b border-border/30 shadow-[0_1px_0_hsl(var(--foreground)/0.04),0_14px_42px_hsl(var(--control-ink)/0.06)]"
          : isDarkTheme
            ? "border-b border-transparent bg-transparent"
            : "border-b border-border/30 bg-background/88 shadow-[0_1px_0_hsl(var(--foreground)/0.04),0_14px_36px_hsl(var(--control-ink)/0.06)] backdrop-blur-xl"
      }`}
    >
      <nav className="mx-auto flex h-14 w-full max-w-[1500px] items-center justify-between px-5 md:px-8 xl:h-16 xl:px-10" aria-label={t("nav.primaryAria")}>
        <Link
          to="/"
          className="focus-ring group flex items-center"
          aria-label={copy.homeAria}
          aria-current={activePathname === "/" ? "page" : undefined}
        >
          <VertLogo className={`h-5 w-auto transition-opacity duration-300 group-hover:opacity-70 xl:h-[22px] ${topText}`} />
          <span className="sr-only">Vert AI</span>
        </Link>

        <div className={`hidden items-center gap-1 border px-2 py-1 transition-colors duration-500 xl:flex ${navShell}`}>
          {renderDesktopDropdown(
            "services",
            t("nav.services"),
            {
              href: "/services",
              label: t("nav.servicesOverview"),
              description: copy.servicesOverview,
            },
            serviceLinks,
          )}
          {directLinks.slice(0, 1).map((link) => {
            const active = isActivePath(activePathname, link.href);
            return (
              <Link
                key={link.href}
                to={link.href}
                aria-current={active ? "page" : undefined}
                className={`focus-ring relative inline-flex h-10 items-center px-3 font-mono text-[11px] font-medium uppercase tracking-[0.1em] transition-colors duration-400 ${topHover} ${
                  active
                    ? scrolled || !isDarkTheme
                      ? "bg-foreground/5 text-foreground"
                      : "bg-[hsl(var(--sd-fg)/0.08)] text-[hsl(var(--sd-fg))]"
                    : topMuted
                }`}
              >
                {link.label}
                {active ? <span className="absolute -bottom-1 left-0 right-0 h-px bg-primary/60" /> : null}
              </Link>
            );
          })}
          {renderDesktopDropdown(
            "industries",
            t("nav.industries"),
            {
              href: "/industries",
              label: t("nav.industriesOverview"),
              description: copy.industriesOverview,
            },
            industryLinks,
            beyondCommerceLink,
          )}

          {directLinks.slice(1).map((link) => {
            const active = isActivePath(activePathname, link.href);
            return (
              <Link
                key={link.href}
                to={link.href}
                aria-current={active ? "page" : undefined}
                className={`focus-ring relative inline-flex h-10 items-center px-3 font-mono text-[11px] font-medium uppercase tracking-[0.1em] transition-colors duration-400 ${topHover} ${
                  active
                    ? scrolled || !isDarkTheme
                      ? "bg-foreground/5 text-foreground"
                      : "bg-[hsl(var(--sd-fg)/0.06)] text-[hsl(var(--sd-fg))]"
                    : topMuted
                }`}
              >
                {link.label}
                {active && <span className="absolute -bottom-1 left-0 right-0 h-px bg-primary/40" />}
              </Link>
            );
          })}

          {mounted && (
            <>
              {renderLocaleSwitch(`focus-ring flex h-10 w-10 items-center justify-center border-l font-mono text-[10px] font-medium tracking-[0.12em] transition-colors duration-400 ${scrolled || !isDarkTheme ? "border-border/45" : "border-[hsl(var(--sd-fg)/0.1)]"} ${topMuted} ${topHover}`)}
              <button
                onClick={toggleTheme}
                className={`focus-ring flex h-10 w-10 items-center justify-center transition-colors duration-400 ${topMuted} ${topHover}`}
                aria-label={copy.themeAria}
              >
                {theme === "dark" ? <Sun className="h-3.5 w-3.5" aria-hidden="true" /> : <Moon className="h-3.5 w-3.5" aria-hidden="true" />}
              </button>
            </>
          )}

          <PrimaryCtaLink
            cta={siteContent.ctas.primary}
            labelVariant="shortLabel"
            dataEventLocation="header_desktop"
            dataEventSurface="primary_navigation"
            className={`focus-ring ml-1 inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap px-5 text-[13px] font-semibold transition-all duration-600 ${
              scrolled || !isDarkTheme
                ? "border border-foreground bg-foreground text-background hover:bg-transparent hover:text-foreground"
                : "border border-[hsl(var(--sd-fg)/0.18)] bg-transparent text-[hsl(var(--sd-fg))] hover:border-primary/55 hover:text-primary"
            }`}
          >
            {primaryCtaLabel}
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </PrimaryCtaLink>
        </div>

        <div className="flex items-center gap-1 xl:hidden">
          {mounted && (
            <>
              {renderLocaleSwitch(`focus-ring flex h-11 w-11 items-center justify-center font-mono text-[10px] font-medium tracking-wider transition-colors ${topMuted} ${topHover}`)}
              <button
                onClick={toggleTheme}
                className={`focus-ring flex h-11 w-11 items-center justify-center transition-colors ${topMuted} ${topHover}`}
                aria-label={copy.themeAria}
              >
                {theme === "dark" ? <Sun className="h-3.5 w-3.5" aria-hidden="true" /> : <Moon className="h-3.5 w-3.5" aria-hidden="true" />}
              </button>
            </>
          )}
          <Sheet
            open={mobileOpen}
            onOpenChange={(open) => {
              if (open) mobileHashNavigationRef.current = false;
              setMobileOpen(open);
            }}
          >
            <SheetTrigger asChild>
              <button
                className={`focus-ring -mr-2 flex h-11 w-11 items-center justify-center ${topText}`}
                aria-label={t("nav.openMenu")}
                aria-controls="mobile-navigation"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent
              id="mobile-navigation"
              side="right"
              closeLabel={t("nav.closeMenu")}
              className="w-[min(92vw,30rem)] overflow-y-auto border-border/55 bg-background p-0 xl:hidden"
              onCloseAutoFocus={(event) => {
                if (!mobileHashNavigationRef.current) return;
                event.preventDefault();
                mobileHashNavigationRef.current = false;
              }}
            >
              <SheetTitle className="sr-only">{t("nav.mobileAria")}</SheetTitle>
              <SheetDescription className="sr-only">
                {copy.mobileNavAria}
              </SheetDescription>
              <div className="border-b border-border/35 px-5 py-[1.125rem]">
                <VertLogo className="h-5 w-auto text-foreground" aria-hidden="true" />
              </div>
              <nav className="px-5 pb-8 pt-5" aria-label={t("nav.mobileAria")}>
                <div className="mb-5 border-b border-border/35 pb-5">
                  <PrimaryCtaLink
                    cta={siteContent.ctas.primary}
                    labelVariant="shortLabel"
                    onClick={() => setMobileOpen(false)}
                    dataEventLocation="header_mobile"
                    dataEventSurface="mobile_navigation"
                    className="focus-ring btn-primary w-full justify-center text-center"
                  >
                    {primaryCtaLabel}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </PrimaryCtaLink>
                </div>
                {renderMobileGroup(
                  t("nav.services"),
                  {
                    href: "/services",
                    label: t("nav.servicesOverview"),
                    description: copy.mobileServicesOverview,
                  },
                  serviceLinks,
                )}
                <Link
                  to="/solutions"
                  onClick={() => setMobileOpen(false)}
                  aria-current={isActivePath(activePathname, "/solutions") ? "page" : undefined}
                  className="focus-ring block min-h-16 border-b border-border/25 py-4 text-foreground"
                >
                  <span className="block font-mono text-[11px] font-semibold uppercase tracking-[0.12em]">
                    {t("nav.solutions")}
                  </span>
                  <span className="mt-1 block text-[13px] leading-6 text-muted-foreground">
                    {directLinks[0].description}
                  </span>
                </Link>
                {renderMobileGroup(
                  t("nav.industries"),
                  {
                    href: "/industries",
                    label: t("nav.industriesOverview"),
                    description: copy.mobileIndustriesOverview,
                  },
                  industryLinks,
                  beyondCommerceLink,
                )}
                <div className="py-3">
                  {directLinks.slice(1).map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileOpen(false)}
                      aria-current={isActivePath(activePathname, link.href) ? "page" : undefined}
                      className={`focus-ring block min-h-[4.25rem] border-b border-border/25 py-4 transition-colors hover:text-foreground ${
                        isActivePath(activePathname, link.href) ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      <span className="block font-mono text-[11px] font-semibold uppercase tracking-[0.12em]">{link.label}</span>
                      <span className="mt-1 block text-[13px] leading-6">{link.description}</span>
                    </Link>
                  ))}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
